/**
 * Upcoming Appointment Action - WHAT Section
 *
 * Edit mode UI for sending upcoming appointment/session reminders.
 * Reminds clients about scheduled appointments with staff.
 *
 * V2 Reference: v2/app/templates/components/client-reminder-appointment.hbs
 *
 * Fields:
 * - delayInterval: Hours before appointment to send
 * - forUserId: Staff member IDs to filter by (optional)
 * - reminderOnId: Service ID to filter by (optional)
 * - reminderOnType: 'Fbm::Service' when service is selected
 */

import { useQuery } from '@tanstack/react-query';
import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { Autocomplete } from 'src/components/Autocomplete';
import { Box } from 'src/components/Box';
import { Stack } from 'src/components/Stack';
import { TextField } from 'src/components/TextField';
import { Typography } from 'src/components/Typography';
import { fetchServicesOptions } from 'src/models/service/service';
import { fetchUserQueryKey } from 'src/models/user/user';
import { fetchUsers } from 'src/models/user/user';
import { getUserDisplayName } from 'src/models/user/utils';

import { ActionWhatSectionProps } from '../automationActions';

/**
 * UpcomingAppointment WHAT Section - Edit mode
 */
export const UpcomingAppointmentWhatSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  // Fetch trainers
  const { data: staffMembers = [] } = useQuery({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),
    queryFn: () => fetchUsers({ fetchTrainers: true }),
  });

  // Fetch services
  const { data: services = [] } = useQuery({
    queryKey: ['services-options'],
    queryFn: fetchServicesOptions,
    staleTime: 60000,
  });

  const selectedStaffIds = Array.isArray(automation.forUserId)
    ? automation.forUserId
    : [];

  const selectedStaff = staffMembers.filter((user) =>
    selectedStaffIds.includes(parseInt(user.id.toString(), 10))
  );

  const selectedService = services.find(
    (s) =>
      automation.reminderOnType === 'Fbm::Service' &&
      s.id.toString() === automation.reminderOnId?.toString()
  );

  return (
    <Stack spacing={2}>
      {/* Staff Filter */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.upcomingAppointment.sessionWith')}</Box>
        <Box sx={{ minWidth: 250 }}>
          <Autocomplete
            multiple
            options={staffMembers}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : getUserDisplayName(option)
            }
            value={selectedStaff}
            onChange={(_, newValue) => {
              setFieldValue(
                `automations.${index}.forUserId`,
                newValue.map((s) => parseInt(s.id.toString(), 10))
              );
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('automations.upcomingAppointment.anyStaff')}
                size="small"
              />
            )}
            size="small"
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Box>
      </Box>

      {/* Service Filter */}
      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
        <Box>{t('automations.upcomingAppointment.forService')}</Box>
        <Box sx={{ minWidth: 200 }}>
          <Autocomplete
            options={services}
            getOptionLabel={(option) =>
              typeof option === 'string' ? option : option.name
            }
            value={selectedService || null}
            onChange={(_, newValue) => {
              if (newValue) {
                setFieldValue(`automations.${index}.reminderOnId`, newValue.id);
                setFieldValue(
                  `automations.${index}.reminderOnType`,
                  'Fbm::Service'
                );
              } else {
                setFieldValue(`automations.${index}.reminderOnId`, null);
                setFieldValue(`automations.${index}.reminderOnType`, null);
              }
            }}
            renderInput={(params) => (
              <TextField
                {...params}
                placeholder={t('automations.upcomingAppointment.anyService')}
              size="small"
              />
            )}
            size="small"
            isOptionEqualToValue={(option, value) => option.id === value.id}
          />
        </Box>
      </Box>
    </Stack>
  );
};

/**
 * UpcomingAppointment What Summary Component
 */
export const UpcomingAppointmentWhatSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();

  // Fetch staff members for display
  const { data: staffMembers = [] } = useQuery({
    queryKey: fetchUserQueryKey({ fetchTrainers: true }),
    queryFn: () => fetchUsers({ fetchTrainers: true }),
  });

  // Fetch services for display
  const { data: services = [] } = useQuery({
    queryKey: ['services-options'],
    queryFn: fetchServicesOptions,
    staleTime: 60000,
  });

  const selectedStaffIds = Array.isArray(automation.forUserId)
    ? automation.forUserId
    : [];

  const staffNames = staffMembers
    .filter((user) => selectedStaffIds.includes(parseInt(user.id.toString(), 10)))
    .map((user) => getUserDisplayName(user))
    .join(', ');

  const selectedService = services.find(
    (s) =>
      automation.reminderOnType === 'Fbm::Service' &&
      s.id.toString() === automation.reminderOnId?.toString()
  );

  const parts: string[] = [];

  if (staffNames) {
    parts.push(
      t('automations.upcomingAppointment.withStaff', { staff: staffNames })
    );
  } else {
    parts.push(t('automations.upcomingAppointment.withAnyStaff'));
  }

  if (selectedService) {
    parts.push(
      t('automations.upcomingAppointment.forServiceName', {
        service: selectedService.name,
      })
    );
  }

  return <>{parts.join(' ')}</>;
};

/**
 * UpcomingAppointment WHEN Section - Edit mode
 * Simple "X hours before" input
 */
export const UpcomingAppointmentWhenSection: FC<ActionWhatSectionProps> = ({
  automation,
  index,
}) => {
  const { t } = useTranslation();
  const { setFieldValue } = useFormikContext();

  return (
    <Box display="flex" alignItems="center" gap={1}>
      <TextField
        type="number"
        value={automation.delayInterval ?? 48}
        onChange={(e) =>
          setFieldValue(
            `automations.${index}.delayInterval`,
            parseInt(e.target.value, 10) || 0
          )
        }
        InputProps={{ inputProps: { min: 1 } }}
        size="small"
        sx={{ width: 80 }}
      />
      <Typography variant="body2">
        {t('automations.upcomingAppointment.hoursBefore')}
      </Typography>
    </Box>
  );
};

/**
 * UpcomingAppointment WHEN Summary
 */
export const UpcomingAppointmentWhenSummary: FC<{
  automation: ActionWhatSectionProps['automation'];
}> = ({ automation }) => {
  const { t } = useTranslation();
  const hours = automation.delayInterval ?? 0;

  return (
    <>
      {t('automations.upcomingAppointment.hoursBeforeAppointment', {
        count: hours,
      })}
    </>
  );
};
